import { serviceMasterData } from '@/data/serviceMaster';
import getAccount from '@/common/getAccount';
import { menuItemType } from '@/types/menu';
import { i18n } from '@/plugins/i18n';
import type { ServiceMasterType } from '@/data/serviceMaster';
import type { MenuItem, MenuItemDetail } from '@/types/menu';

const { t } = i18n.global;

type ServiceLinkData = {
  displayName: string;
  domain?: string;
  dispOrder?: number;
};

/** 遷移先ドメインが設定済みのサービス */
type LinkableService = ServiceLinkData & { domain: string };

// Kept for the license-based service list; currently replaced by the static Pro link in getGlobalMenuItems.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getServiceMenuItems = (): MenuItemDetail[] => {
  const menuItems: MenuItemDetail[] = [];
  const serviceDataMap: Record<string, ServiceLinkData> = {};

  const { licenseList } = getAccount.getLicenseAndAuthorityList();

  serviceMasterData.forEach((service: ServiceMasterType) => {
    serviceDataMap[service.id] = {
      displayName: service.nameKey ? t(service.nameKey) : service.name,
      domain: service.domain,
      dispOrder: service.dispOrder,
    };
  });

  // Collect services based on license list
  // ドメイン未設定のサービス (commonなど) はリンクを作れないため除外する
  const licensedServices = licenseList
    .map((serviceId) => serviceDataMap[serviceId])
    .filter((service): service is LinkableService => Boolean(service?.domain));

  // Sort by display order
  licensedServices.sort(
    (firstService, secondService) =>
      (firstService.dispOrder ?? Infinity) -
      (secondService.dispOrder ?? Infinity),
  );

  // Build menu items in order
  licensedServices.forEach((service) => {
    menuItems.push(getServiceLicenseLink(service));
  });

  return menuItems;
};

// regional-serviceでは各サービスのドメインが固定のため、組織IDのサブドメインは付与しない
const getServiceLicenseLink = (
  serviceData: LinkableService,
): MenuItemDetail => {
  return {
    link: serviceData.domain,
    text: serviceData.displayName,
  };
};

export const getGlobalMenuItems = (): MenuItem[] => {
  return [
    {
      type: menuItemType.mono,
      iconName: 'User',
      heading: t('navigation.userInfo'),
      link: '/',
    },
    {
      type: menuItemType.other,
      iconName: 'Service',
      heading: t('navigation.services'),
      link: '/service',
      isOpen: true,
      // NOTE: regional-service delivers one Spectee Pro per country, so the license-based service list is not used for now.
      // details: getServiceMenuItems(),
      details: [
        // Spectee Pro of the deployment country (ph / vn); the URL comes from the build's .env.<mode> file
        {
          link: import.meta.env.VITE_APP_SPECTEE_PRO_DOMAIN,
          text: t('services.pro'),
        },
      ],
    },
    {
      type: menuItemType.other,
      iconName: 'Other',
      heading: t('navigation.others'),
      link: '/other',
      isOpen: true,
      details: [
        // NOTE: The inquiry screen is not needed in the account application, so this code is commented out.
        // { link: '/other/inquiry', text: t('navigation.inquiry') },
        {
          link: import.meta.env.VITE_APP_PRIVACY_POLICY,
          text: t('navigation.privacyPolicy'),
        },
      ],
    },
  ];
};
